import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import {
  MarkAllReadResponse,
  Notification,
  NotificationListResponse,
  UnreadCountResponse,
} from "@models/notification";
import { Page } from "@models/page";
import { catchError, map, Observable, of } from "rxjs";
import { environment } from "../../environments/environment";
import { SnackBarService } from "./snack-bar.service";

export interface NotificationPage {
  page: Page<Notification>;
  unreadCount: number;
}

@Injectable({
  providedIn: "root",
})
export class NotificationService {
  private readonly baseUrl = `${environment.API_BACKEND}/notifications`;

  private readonly http = inject(HttpClient);
  private readonly snackbarService = inject(SnackBarService);

  /**
   * Lista notificações paginadas. Normaliza a meta do backend
   * (total/page/per_page/unread_count) para o Page<T> padrão e expõe
   * unread_count separadamente.
   */
  list(
    page = 1,
    perPage = 20,
    unreadOnly = false,
  ): Observable<NotificationPage> {
    let params = new HttpParams().set("page", page).set("per_page", perPage);

    if (unreadOnly) {
      params = params.set("unread", true);
    }

    return this.http
      .get<NotificationListResponse>(this.baseUrl, { params })
      .pipe(
        map((response) => this.normalize(response)),
        catchError(() => {
          this.snackbarService.openSnackBar(
            "Erro ao carregar notificações",
            "Fechar",
            "error",
          );
          return of(this.emptyPage(perPage));
        }),
      );
  }

  unreadCount(): Observable<number> {
    return this.http
      .get<UnreadCountResponse>(`${this.baseUrl}/unread_count`)
      .pipe(
        map((response) => response.unread_count),
        catchError(() => of(0)),
      );
  }

  markRead(id: number): Observable<Notification | null> {
    return this.http
      .patch<Notification>(`${this.baseUrl}/${id}/mark_read`, {})
      .pipe(catchError(() => of(null)));
  }

  markAllRead(): Observable<number> {
    return this.http
      .post<MarkAllReadResponse>(`${this.baseUrl}/mark_all_read`, {})
      .pipe(
        map((response) => response.affected),
        catchError(() => {
          this.snackbarService.openSnackBar(
            "Erro ao marcar notificações como lidas",
            "Fechar",
            "error",
          );
          return of(0);
        }),
      );
  }

  private normalize(response: NotificationListResponse): NotificationPage {
    const { total, page, per_page, unread_count } = response.meta;
    const pageSize = per_page || 20;

    return {
      page: {
        data: response.data ?? [],
        meta: {
          total_count: total,
          total_pages: pageSize > 0 ? Math.ceil(total / pageSize) : 0,
          current_page: page,
          page_size: pageSize,
        },
      },
      unreadCount: unread_count ?? 0,
    };
  }

  private emptyPage(perPage: number): NotificationPage {
    return {
      page: {
        data: [],
        meta: {
          total_count: 0,
          total_pages: 0,
          current_page: 1,
          page_size: perPage,
        },
      },
      unreadCount: 0,
    };
  }
}
